import {axiosClientAuth} from '../../utils/axiosClient'

export const KYC_QUERY_KEY = 'kyc-list'

export interface SmsPreference {
  sms_type?: string
  volume_min?: number | null
  volume_max?: number | null
  frequency?: string
}

interface KybDocument {
  document_id: string
  document_type: string
  s3_object_uri: string
  file_name: string
  status: string
}

interface KybRecord {
  id: string
  registration_number: string
  email: string
  business_name: string
  brand_name: string
  resource_type: string
  business_type: string
  category: string
  department: string
  address_line1: string
  address_line2: string
  city: string
  state: string
  zip_code: string
  country: string
  authorised_person_name: string
  authorised_person_id_type?: string
  authorised_person_id_document_uri?: string
  authorised_person_id_document_file_name?: string
  contact_phone: string
  owner_id: string | null
  description?: string
  sms_type?: string
  sms_preferences?: SmsPreference[]
  status: string
  documents: KybDocument[]
  created_at: string
}

export interface KYCRecord {
  uuid: string
  legalEntityName: string
  brandName: string
  ownerId: string | null
  businessType: string
  resourceType: string
  registrationNumber: string
  category: string
  department: string
  address: string
  city: string
  country: string
  postalCode: string
  authorisedPersonName: string
  authorisedIdType?: string
  authorisedIdFile?: string
  authorisedIdFileName?: string
  contactNumber: string
  emailAddress: string
  description?: string
  smsType?: string
  smsPreferences?: SmsPreference[]
  submittedAt: string
  status: 'pending' | 'approved' | 'rejected'
  documents: {name: string; url: string; fileName: string; id: string}[]
}

const DOCUMENT_TYPE_LABELS: Record<string, string> = {
  ENT_REGN_CERT: 'Enterprise Registration Certificate',
  TAX_INDT_NO: 'Taxpayer Identification Number (TIN)',
  VAT_REGN: 'VAT Registration / Certificate',
}

function mapStatus(apiStatus: string): 'pending' | 'approved' | 'rejected' {
  if (apiStatus === 'ACCEPTED') return 'approved'
  if (apiStatus === 'REJECTED') return 'rejected'
  return 'pending'
}

function mapKybRecord(record: KybRecord): KYCRecord {
  const addressParts = [record.address_line1, record.address_line2].filter(Boolean)
  return {
    uuid: record.id,
    legalEntityName: record.business_name,
    brandName: record.brand_name,
    ownerId: record.owner_id,
    businessType: record.business_type,
    resourceType: record.resource_type,
    registrationNumber: record.registration_number,
    category: record.category,
    department: record.department,
    address: addressParts.join(', '),
    city: record.city,
    country: record.country,
    postalCode: record.zip_code,
    authorisedPersonName: record.authorised_person_name,
    authorisedIdType: record.authorised_person_id_type,
    authorisedIdFile: record.authorised_person_id_document_uri,
    authorisedIdFileName: record.authorised_person_id_document_file_name,
    contactNumber: record.contact_phone,
    emailAddress: record.email,
    description: record.description,
    smsType: record.sms_type,
    smsPreferences: record.sms_preferences ?? [],
    submittedAt: record.created_at,
    status: mapStatus(record.status),
    documents: record.documents.map((doc) => ({
      name: DOCUMENT_TYPE_LABELS[doc.document_type] ?? doc.document_type,
      url: doc.s3_object_uri,
      fileName: doc.file_name,
      id: doc.document_id,
    })),
  }
}

export async function fetchKYCList(params: {
  page: number
  page_size: number
  search?: string
  status?: string
}) {
  const res = await axiosClientAuth.get('/kyb/', {params})
  const {count, results} = res.data.data
  return {
    count: count as number,
    results: (results as KybRecord[]).map(mapKybRecord),
  }
}
